import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  EnvelopeIcon,
  FlagIcon,
  SpinnerIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Currency } from '@/components/common/Currency';
import { useToast } from 'src/context/ToastContext';
import useAuth from '@/api/auth/useAuth';
import { useArCustomerContacts } from '@/api/rpc/hooks/accounts-receivable/useArCustomerContacts';
import { useArInvoiceEmail } from '@/api/rpc/hooks/accounts-receivable/useArInvoiceEmail';
import { HtmlWYSIWYGEditor } from '@/components/common/HtmlWYSIWYGEditor';
import { EmailRecipientsPanel } from '@/components/common/email-composer/EmailRecipientsPanel';
import {
  EMAIL_REGEX,
  dedupeContactsByEmail,
} from '@/components/common/email-composer/types';
import { templateVariableMarkersToMustache } from '@/components/common/lexical-template-variables/TemplateVariableNode';
import { useBulkItemSendFlow } from '@/components/common/bulk-send/useBulkItemSendFlow';
import { BulkSendRail } from '@/components/common/bulk-send/BulkSendRail';
import { BulkSendSummaryList } from '@/components/common/bulk-send/BulkSendSummaryList';
import { PdfPreviewAside } from '@/components/common/bulk-send/PdfPreviewAside';
import type {
  BulkSendDraft,
  BulkSendItem,
} from '@/components/common/bulk-send/types';
import {
  getLineTypeBadgeVariant,
  formatLineType,
} from './ar-invoice-invoice-lines';
import { T, Var, useGT } from 'gt-react';
import type { EmailImportance, InvoiceLineItemDetail } from '@erp/rpc';

// ─── Types ───────────────────────────────────────────────────────────────────

interface SendInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoices: Array<{
    invoiceId: string;
    customerId: string;
    customerName: string | null;
    invoiceNumber: string | null;
  }>;
}

interface InvoiceBulkSendItem extends BulkSendItem {
  invoiceNumber: string | null;
  customerName: string | null;
}

interface InvoiceExtra {
  excludedLineItemIds: Set<string>;
}

const PLACEHOLDER_EMAIL = 'preview@placeholder.local';
const INITIAL_EXTRA: InvoiceExtra = { excludedLineItemIds: new Set() };

// ─── Component ───────────────────────────────────────────────────────────────

export function SendInvoiceDialog({
  open,
  onOpenChange,
  invoices,
}: SendInvoiceDialogProps) {
  const gt = useGT();
  const { showToast } = useToast();
  const { currentUser } = useAuth();
  const senderEmail = currentUser?.emails[0] ?? null;

  const items: InvoiceBulkSendItem[] = useMemo(
    () =>
      invoices.map((inv) => ({
        id: inv.invoiceId,
        customerId: inv.customerId,
        displayName: inv.invoiceNumber,
        invoiceNumber: inv.invoiceNumber,
        customerName: inv.customerName,
      })),
    [invoices],
  );

  const { send, isSending, preview, isPreviewing, previewData, resetPreview } =
    useArInvoiceEmail();

  const flow = useBulkItemSendFlow<InvoiceBulkSendItem, InvoiceExtra>({
    items,
    initialExtra: INITIAL_EXTRA,
    onNavigate: resetPreview,
  });

  const { contacts, isLoading: contactsLoading } = useArCustomerContacts(
    flow.currentItem?.customerId ?? '',
  );
  const dedupedContacts = dedupeContactsByEmail(contacts);

  // flow.bccEmails covers only contact/custom BCC roles, so fold in the
  // sender-self address when "BCC me" is on — otherwise the confirm step
  // under-reports who actually receives the email. Deduped case-insensitively
  // the same way the server merges BCC (see sendArInvoiceEmail), so a user who
  // also picked their own address as a BCC recipient sees it once, exactly as
  // it will be sent.
  const confirmBccEmails = [
    ...new Map(
      [
        ...(flow.bccSender && senderEmail ? [senderEmail] : []),
        ...flow.bccEmails,
      ].map((email) => [email.toLowerCase(), email]),
    ).values(),
  ];

  const [previewError, setPreviewError] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [previewTab, setPreviewTab] = useState('lineItems');

  const [displayPreview, setDisplayPreview] = useState<typeof previewData>(
    () => previewData,
  );
  const [allLineItems, setAllLineItems] = useState<InvoiceLineItemDetail[]>([]);

  const fetchGenRef = useRef(0);
  const currentItemId = flow.currentItem?.id;

  const fetchPreview = useCallback(() => {
    if (!currentItemId) return;
    setPreviewError(false);
    const gen = ++fetchGenRef.current;
    const excludedIds = flow.extra.excludedLineItemIds;
    const hasExclusions = excludedIds.size > 0;
    preview({
      invoiceId: currentItemId,
      recipientEmails: [PLACEHOLDER_EMAIL],
      ...(hasExclusions && { excludeLineItemIds: Array.from(excludedIds) }),
      ...(flow.selectedSendFrom && { sendFrom: flow.selectedSendFrom }),
    })
      .then((data) => {
        if (fetchGenRef.current !== gen) return;
        setDisplayPreview(data);
        if (!hasExclusions) setAllLineItems(data.lineItems);
      })
      .catch(() => {
        if (fetchGenRef.current === gen) setPreviewError(true);
      });
  }, [currentItemId, flow.extra, flow.selectedSendFrom, preview]);

  const lastFetchedItemId = useRef<string | null>(null);
  useEffect(() => {
    if (!open || flow.phase !== 'review') return;
    if (!currentItemId) return;
    if (lastFetchedItemId.current !== currentItemId) {
      lastFetchedItemId.current = currentItemId;
      resetPreview();
      fetchPreview();
      return;
    }
    const timer = setTimeout(fetchPreview, 600);
    return () => clearTimeout(timer);
  }, [open, flow.phase, currentItemId, fetchPreview, resetPreview]);

  // Seed per-item email drafts on first preview. Skipped when a draft already
  // exists for this item (restored by the engine on entry).
  const seededDraftItems = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!open) return;
    if (!previewData || isPreviewing) return;
    if (!currentItemId) return;
    if (seededDraftItems.current.has(currentItemId)) return;
    seededDraftItems.current.add(currentItemId);
    if (flow.hasDraftFor(currentItemId)) return;
    flow.setSubjectDraft(previewData.subject);
    flow.setBodyDraft(previewData.htmlContent);
    flow.setPdfFilenameDraft(previewData.pdfFilename);
    flow.seededSubjectRef.current = previewData.subject;
    const defaultExcludedIds = previewData.lineItems
      .filter((li) => li.lineType !== 'product')
      .map((li) => li.id);
    if (defaultExcludedIds.length > 0) {
      flow.setExtra({ excludedLineItemIds: new Set(defaultExcludedIds) });
    }
  }, [open, previewData, isPreviewing, currentItemId, flow]);

  function resetLocalTransientState() {
    setPreviewError(false);
    setShowConfirm(false);
    setPreviewTab('lineItems');
    setAllLineItems([]);
  }

  // ── Send-all helpers ─────────────────────────────────────────────────────

  function getDraftRecipients(draft: BulkSendDraft<InvoiceExtra>): string[] {
    const tos: string[] = [];
    const ccs: string[] = [];
    for (const [email, role] of draft.selectedEmails) {
      if (role === 'to') tos.push(email);
      else if (role === 'cc') ccs.push(email);
    }
    for (const r of draft.customRecipients) {
      const trimmed = r.email.trim();
      if (!EMAIL_REGEX.test(trimmed)) continue;
      if (r.role === 'to') tos.push(trimmed);
      else if (r.role === 'cc') ccs.push(trimmed);
    }
    if (tos.length === 0) return [];
    return [...tos, ...ccs];
  }

  function getDraftBccRecipients(draft: BulkSendDraft<InvoiceExtra>): string[] {
    const bccs: string[] = [];
    for (const [email, role] of draft.selectedEmails) {
      if (role === 'bcc') bccs.push(email);
    }
    for (const r of draft.customRecipients) {
      const trimmed = r.email.trim();
      if (r.role === 'bcc' && EMAIL_REGEX.test(trimmed)) bccs.push(trimmed);
    }
    return bccs;
  }

  function buildDraftSendArgs(
    invoiceId: string,
    draft: BulkSendDraft<InvoiceExtra>,
    recipients: string[],
  ): Parameters<typeof send>[0] {
    const trimmedSubject = draft.subjectDraft.trim();
    const trimmedBody = templateVariableMarkersToMustache(
      draft.bodyDraft,
    ).trim();
    const trimmedPdfFilename = draft.pdfFilenameDraft.trim();
    const draftSendFrom = flow.getSendFromForKey(draft.selectedSendFromKey);
    const draftBcc = getDraftBccRecipients(draft);
    return {
      invoiceId,
      recipientEmails: recipients,
      ...(draft.extra.excludedLineItemIds.size > 0 && {
        excludeLineItemIds: Array.from(draft.extra.excludedLineItemIds),
      }),
      ...(trimmedSubject && { subjectOverride: trimmedSubject }),
      ...(trimmedBody && { htmlContentOverride: trimmedBody }),
      ...(trimmedPdfFilename && { pdfFilenameOverride: trimmedPdfFilename }),
      editedBody: flow.editedBody,
      ...(draftSendFrom && { sendFrom: draftSendFrom }),
      bccSender: draft.bccSender,
      ...(draftBcc.length > 0 && { bccRecipientEmails: draftBcc }),
      ...(flow.importance !== 'normal' && { importance: flow.importance }),
    };
  }

  async function handleConfirmedSend() {
    if (!flow.hasRecipients || !flow.currentItem) return;
    const trimmedSubject = flow.subjectDraft.trim();
    const trimmedBody = templateVariableMarkersToMustache(
      flow.bodyDraft,
    ).trim();
    const trimmedPdfFilename = flow.pdfFilenameDraft.trim();
    try {
      await send({
        invoiceId: flow.currentItem.id,
        recipientEmails: flow.allRecipients,
        ...(flow.extra.excludedLineItemIds.size > 0 && {
          excludeLineItemIds: Array.from(flow.extra.excludedLineItemIds),
        }),
        ...(trimmedSubject && { subjectOverride: trimmedSubject }),
        ...(trimmedBody && { htmlContentOverride: trimmedBody }),
        ...(trimmedPdfFilename && { pdfFilenameOverride: trimmedPdfFilename }),
        editedBody: flow.editedBody,
        ...(flow.selectedSendFrom && { sendFrom: flow.selectedSendFrom }),
        bccSender: flow.bccSender,
        ...(flow.bccEmails.length > 0 && {
          bccRecipientEmails: flow.bccEmails,
        }),
        ...(flow.importance !== 'normal' && { importance: flow.importance }),
      });
      if (flow.isSingle) {
        setShowConfirm(false);
        handleOpenChange(false);
        showToast({
          title: gt('Invoice sent', { context: 'toast notification title' }),
          message: gt('Invoice emailed to {recipients}', {
            context: 'toast notification message',
            recipients: flow.allRecipients.join(', '),
          }),
          color: 'success',
          icon: 'check',
        });
        return;
      }
      flow.recordResult(flow.currentItem.id, {
        status: 'sent',
        itemName: flow.currentItem.displayName,
      });
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : gt('Failed to send invoice', {
              context: 'error message when invoice send fails',
            });
      if (flow.isSingle) {
        showToast({
          title: gt('Send failed', {
            context: 'toast notification title when invoice send fails',
          }),
          message,
          color: 'error',
          icon: 'warning',
        });
        return;
      }
      flow.recordResult(flow.currentItem.id, {
        status: 'failed',
        itemName: flow.currentItem.displayName,
        error: message,
      });
    }
    flow.advanceToNext();
  }

  const unvisitedInvoices = items.filter((item, i) => {
    const status = flow.results.get(item.id)?.status;
    if (status === 'sent' || status === 'canceled') return false;
    if (flow.hasDraftFor(item.id)) return false;
    if (i === flow.currentIndex) return false;
    return true;
  });
  const remainingCount = items.filter((item) => {
    const status = flow.results.get(item.id)?.status;
    return status !== 'sent' && status !== 'canceled';
  }).length;

  async function handleSendAll() {
    flow.setShowSendAllConfirm(false);

    const drafts = new Map(flow.itemDrafts);
    if (flow.currentItem) {
      const liveDraft = flow.captureCurrentDraft();
      drafts.set(flow.currentItem.id, liveDraft);
      flow.persistCurrentDraft();
    }

    flow.setIsSendingAll(true);
    flow.setBulkSendProgress(0);

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const status = flow.results.get(item.id)?.status;
      if (status === 'sent' || status === 'canceled') {
        flow.setBulkSendProgress((prev) => prev + 1);
        continue;
      }

      try {
        const draft = drafts.get(item.id);
        const draftRecipients = draft ? getDraftRecipients(draft) : [];
        if (!draft || draftRecipients.length === 0) {
          // No draft, or draft has no recipients — no server-side defaults
          // exist for invoices, so this item can't be auto-sent.
          flow.recordResult(item.id, {
            status: 'skipped',
            itemName: item.displayName,
          });
          flow.setBulkSendProgress((prev) => prev + 1);
          continue;
        }
        const payload = buildDraftSendArgs(item.id, draft, draftRecipients);
        await send(payload);
        flow.recordResult(item.id, {
          status: 'sent',
          itemName: item.displayName,
        });
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : gt('Failed to send invoice', {
                context: 'error message when invoice send fails',
              });
        flow.recordResult(item.id, {
          status: 'failed',
          itemName: item.displayName,
          error: message,
        });
      }
      flow.setBulkSendProgress((prev) => prev + 1);
    }

    flow.setIsSendingAll(false);
    flow.resetTransientState();
    resetLocalTransientState();
    flow.setPhase('summary');
  }

  // ── Dialog open/close ────────────────────────────────────────────────────

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen && flow.isSendingAll) return;
    if (!nextOpen) {
      flow.resetAll();
      resetLocalTransientState();
      lastFetchedItemId.current = null;
      seededDraftItems.current.clear();
      setDisplayPreview(null);
    }
    onOpenChange(nextOpen);
  }

  function handleRailSelect(index: number) {
    if (index === flow.currentIndex && flow.phase === 'review') return;
    resetLocalTransientState();
    flow.viewItem(index);
  }

  function handleRevisit(index: number) {
    resetLocalTransientState();
    flow.revisitItem(index);
  }

  // ── Render ────────────────────────────────────────────────────────────────

  if (!flow.currentItem && flow.phase === 'review') return null;

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
      // Radix guarded dismissal on the content with `onInteractOutside` /
      // `onEscapeKeyDown` + `preventDefault`. Base UI reports the reason on the
      // root's change event, so the guard lives here now.
      disableBackdropClickClose={flow.isSendingAll}
      disableEscapeKeyClose={flow.isSendingAll}
    >
      <DialogContent
        className="sm:max-w-[min(1900px,97vw)] h-[90vh] flex flex-col"
        showCloseButton={!flow.isSendingAll}
      >
        <div className="flex flex-1 min-h-0 gap-4">
          {/* ── Left rail ───────────────────────────────────────── */}
          <BulkSendRail
            eyebrowLabel={gt('Bulk send · Invoices', {
              context: 'bulk send dialog left rail eyebrow label',
            })}
            itemNounOne={gt('invoice', {
              context: 'left rail item noun, singular',
            })}
            itemNounOther={gt('invoices', {
              context: 'left rail item noun, plural',
            })}
            items={items}
            results={flow.results}
            currentIndex={flow.currentIndex}
            phase={flow.phase}
            isSingle={flow.isSingle}
            isSendingAll={flow.isSendingAll}
            bulkSendProgress={flow.bulkSendProgress}
            onSelect={handleRailSelect}
          />

          {/* ── Right column ────────────────────────────────────── */}
          <div className="flex-1 min-h-0 flex flex-col gap-4">
            <DialogHeader>
              {flow.phase === 'summary' ? (
                <DialogTitle>
                  <T context="dialog title, bulk invoice send finished">
                    Bulk Send Complete
                  </T>
                </DialogTitle>
              ) : (
                <div className="flex items-baseline gap-3">
                  <DialogTitle className="text-2xl font-semibold tracking-tight">
                    {flow.currentItem.invoiceNumber ??
                      gt('(Unknown)', {
                        context: 'unknown invoice number placeholder',
                      })}
                    {flow.currentItem.customerName
                      ? ` — ${flow.currentItem.customerName}`
                      : ''}
                  </DialogTitle>
                  {!flow.isSingle && (
                    <span className="shrink-0 rounded bg-muted px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      <T context="dialog header progress badge, e.g. '2 of 3'">
                        <Var>{String(flow.currentIndex + 1)}</Var> of{' '}
                        <Var>{String(items.length)}</Var>
                      </T>
                    </span>
                  )}
                </div>
              )}
            </DialogHeader>

            {/* ── Review Phase ──────────────────────────────────────── */}
            {flow.phase === 'review' && (
              <>
                <div className="min-h-0 flex-1 flex flex-col gap-4 overflow-hidden">
                  <EmailRecipientsPanel
                    key={flow.currentItem.id}
                    contactsLoading={contactsLoading}
                    contacts={dedupedContacts}
                    selectedEmails={flow.selectedEmails}
                    customRecipients={flow.customRecipients}
                    addCustomRecipient={flow.addCustomRecipient}
                    addContactToRole={flow.addContactToRole}
                    removeCustomRecipient={flow.removeCustomRecipient}
                    removeContact={flow.removeContact}
                    toggleEmail={flow.toggleEmail}
                    toggleEmailRole={flow.toggleEmailRole}
                    bccSender={flow.bccSender}
                    setBccSender={flow.setBccSender}
                    senderEmail={senderEmail}
                    sendFromOptions={flow.sendFromOptions}
                    selectedSendFromKey={flow.selectedSendFromKey}
                    setSelectedSendFromKey={flow.setSelectedSendFromKey}
                  />

                  {!displayPreview && isPreviewing ? (
                    <div className="flex flex-1 min-h-0 items-center justify-center gap-2 text-sm text-muted-foreground">
                      <SpinnerIcon className="h-4 w-4 animate-spin" />
                      <T context="send invoice preview loading state">
                        Loading preview...
                      </T>
                    </div>
                  ) : !displayPreview && previewError ? (
                    <div className="flex flex-1 min-h-0 items-center justify-center gap-2 text-sm text-muted-foreground">
                      <WarningIcon className="h-4 w-4" />
                      <T context="send invoice preview error state">
                        Failed to load preview
                      </T>
                    </div>
                  ) : displayPreview ? (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-muted-foreground shrink-0">
                          <T context="email header Subject field">Subject:</T>
                        </span>
                        <Input
                          value={flow.subjectDraft}
                          onChange={(e) => flow.setSubjectDraft(e.target.value)}
                          className="h-7 text-xs"
                        />
                        {isPreviewing && (
                          <SpinnerIcon className="h-3 w-3 animate-spin shrink-0" />
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-muted-foreground shrink-0">
                          <T context="email PDF attachment filename field label">
                            Attachment:
                          </T>
                        </span>
                        <Input
                          value={flow.pdfFilenameDraft}
                          onChange={(e) =>
                            flow.setPdfFilenameDraft(e.target.value)
                          }
                          className="h-7 text-xs"
                          placeholder={gt('invoice.pdf', {
                            context: 'placeholder for PDF attachment filename',
                          })}
                        />
                      </div>
                      <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                        <Checkbox
                          checked={flow.importance === 'high'}
                          onCheckedChange={(v) =>
                            flow.setImportance(
                              (v === true
                                ? 'high'
                                : 'normal') as EmailImportance,
                            )
                          }
                        />
                        <FlagIcon
                          weight={
                            flow.importance === 'high' ? 'fill' : 'regular'
                          }
                          className={`h-3.5 w-3.5 ${
                            flow.importance === 'high'
                              ? 'text-destructive'
                              : 'text-muted-foreground'
                          }`}
                        />
                        <span
                          className={
                            flow.importance === 'high'
                              ? 'font-medium text-destructive'
                              : 'text-muted-foreground'
                          }
                        >
                          <T context="checkbox, mark outgoing invoice email as high priority">
                            Mark as high priority
                          </T>
                        </span>
                      </label>
                      <Tabs
                        value={previewTab}
                        onValueChange={setPreviewTab}
                        className="min-h-0 flex-1 flex flex-col"
                      >
                        <TabsList className="w-fit">
                          <TabsTrigger value="lineItems">
                            <T context="send invoice preview tab">Line Items</T>
                            {allLineItems.length > 0 && (
                              <span className="ml-1.5 text-muted-foreground">
                                (
                                {allLineItems.length -
                                  flow.extra.excludedLineItemIds.size}
                                )
                              </span>
                            )}
                          </TabsTrigger>
                          <TabsTrigger value="email">
                            <T context="send invoice preview tab">Email Body</T>
                          </TabsTrigger>
                        </TabsList>
                        <TabsContent
                          value="email"
                          className="min-h-0 flex-1 overflow-auto"
                        >
                          <HtmlWYSIWYGEditor
                            key={flow.currentItem.id}
                            initialHtml={
                              flow.bodyDraft || displayPreview.htmlContent
                            }
                            onChange={flow.handleBodyChange}
                            height={360}
                          />
                        </TabsContent>
                        <TabsContent
                          value="lineItems"
                          className="min-h-0 flex-1 flex flex-col rounded border overflow-auto"
                        >
                          <div className="px-3 py-2 border-b bg-background text-xs text-muted-foreground shrink-0">
                            <T context="send invoice line item list count">
                              <Var>
                                {allLineItems.length -
                                  flow.extra.excludedLineItemIds.size}
                              </Var>{' '}
                              of <Var>{allLineItems.length}</Var> line items
                              included
                            </T>
                            {flow.extra.excludedLineItemIds.size > 0 && (
                              <button
                                type="button"
                                className="ml-2 text-primary hover:underline"
                                onClick={() =>
                                  flow.setExtra({
                                    excludedLineItemIds: new Set(),
                                  })
                                }
                              >
                                <T context="button label, include all line items in invoice send">
                                  Include all
                                </T>
                              </button>
                            )}
                          </div>
                          {allLineItems.length === 0 ? (
                            <div className="py-8 text-center text-sm text-muted-foreground">
                              <T context="send invoice line item list empty state">
                                No line items on this invoice
                              </T>
                            </div>
                          ) : (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="w-10" />
                                  <TableHead>
                                    <T context="table column header for invoice line item type">
                                      Type
                                    </T>
                                  </TableHead>
                                  <TableHead>
                                    <T context="table column header for invoice line item description">
                                      Description
                                    </T>
                                  </TableHead>
                                  <TableHead className="text-right">
                                    <T context="table column header for invoice line item quantity">
                                      Qty
                                    </T>
                                  </TableHead>
                                  <TableHead className="text-right">
                                    <T context="table column header for invoice line item rate">
                                      Rate
                                    </T>
                                  </TableHead>
                                  <TableHead className="text-right">
                                    <T context="table column header for invoice line item amount">
                                      Amount
                                    </T>
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {allLineItems.map((item) => {
                                  const included =
                                    !flow.extra.excludedLineItemIds.has(
                                      item.id,
                                    );
                                  return (
                                    <TableRow
                                      key={item.id}
                                      className={!included ? 'opacity-50' : ''}
                                    >
                                      <TableCell>
                                        <Checkbox
                                          checked={included}
                                          onCheckedChange={() => {
                                            flow.setExtra((prev) => {
                                              const next = new Set(
                                                prev.excludedLineItemIds,
                                              );
                                              if (next.has(item.id)) {
                                                next.delete(item.id);
                                              } else {
                                                next.add(item.id);
                                              }
                                              return {
                                                excludedLineItemIds: next,
                                              };
                                            });
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Badge
                                          variant={getLineTypeBadgeVariant(
                                            item.lineType,
                                          )}
                                        >
                                          {formatLineType(item.lineType)}
                                        </Badge>
                                      </TableCell>
                                      <TableCell className="text-sm">
                                        {item.description ?? '—'}
                                      </TableCell>
                                      <TableCell className="text-right text-sm">
                                        {item.quantity ?? '—'}
                                      </TableCell>
                                      <TableCell className="text-right text-sm">
                                        <Currency
                                          value={item.rate}
                                          fallback="—"
                                        />
                                      </TableCell>
                                      <TableCell className="text-right text-sm font-bold">
                                        <Currency value={item.amount} />
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          )}
                        </TabsContent>
                      </Tabs>
                    </>
                  ) : null}
                </div>

                <DialogFooter className="flex justify-between sm:justify-between">
                  <div className="flex items-center gap-2">
                    {!flow.isSingle && (
                      <Button
                        variant="outline"
                        onClick={flow.goBack}
                        disabled={!flow.canGoBack || flow.isSendingAll}
                      >
                        <T context="button label, go back to previous invoice in bulk send">
                          Back
                        </T>
                      </Button>
                    )}
                    {!flow.isSingle && (
                      <Button
                        variant="outline"
                        onClick={flow.handleCancel}
                        disabled={flow.isSendingAll}
                      >
                        <T context="button label, explicitly mark this invoice as do-not-send in bulk flow">
                          Cancel Send
                        </T>
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={
                        flow.isSingle
                          ? () => handleOpenChange(false)
                          : flow.handleSkip
                      }
                      disabled={flow.isSendingAll}
                    >
                      {flow.isSingle
                        ? gt('Cancel', {
                            context: 'button label, cancel send invoice dialog',
                          })
                        : gt('Skip', {
                            context:
                              'button label, skip this invoice in bulk send',
                          })}
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    {!flow.isSingle && (
                      <Button
                        variant="outline"
                        onClick={() => flow.setShowSendAllConfirm(true)}
                        disabled={flow.isSendingAll}
                      >
                        {flow.isSendingAll ? (
                          <>
                            <SpinnerIcon className="mr-1.5 h-4 w-4 animate-spin" />
                            <T context="button label, bulk send in progress">
                              Sending All...
                            </T>
                          </>
                        ) : (
                          <T context="button label, send all remaining invoices">
                            Send All
                          </T>
                        )}
                      </Button>
                    )}
                    <Button
                      onClick={() => setShowConfirm(true)}
                      disabled={!flow.hasRecipients || flow.isSendingAll}
                    >
                      <EnvelopeIcon className="mr-1.5 h-4 w-4" />
                      <T context="button label, send AR invoice email">
                        Send Invoice
                      </T>
                    </Button>
                  </div>
                </DialogFooter>
              </>
            )}

            {/* ── Summary Phase ─────────────────────────────────────── */}
            {flow.phase === 'summary' && (
              <>
                <BulkSendSummaryList
                  items={items}
                  results={flow.results}
                  onRevisit={handleRevisit}
                />
                <DialogFooter>
                  <Button onClick={() => handleOpenChange(false)}>
                    <T context="button label, close bulk send summary dialog">
                      Done
                    </T>
                  </Button>
                </DialogFooter>
              </>
            )}
          </div>

          {/* ── PDF preview column ──────────────────────────────── */}
          {flow.phase === 'review' && (
            <PdfPreviewAside
              pdfBase64={displayPreview?.pdfBase64 ?? null}
              isPreviewing={isPreviewing}
              previewError={previewError}
              subheading={gt('reflects line item selections', {
                context: 'pdf preview column subheading',
              })}
            />
          )}
        </div>
      </DialogContent>

      {/* ── Confirm dialog ──────────────────────────────────── */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              <T context="confirm send invoice dialog title">Confirm Send</T>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-1 text-sm">
            {displayPreview && (
              <div className="flex gap-2">
                <span className="w-12 shrink-0 font-medium text-muted-foreground">
                  <T context="email header From field">From:</T>
                </span>
                <span className="text-foreground">{displayPreview.from}</span>
              </div>
            )}
            {flow.toEmails.length > 0 && (
              <div className="flex gap-2">
                <span className="w-12 shrink-0 font-medium text-muted-foreground">
                  <T context="email header To field">To:</T>
                </span>
                <span className="text-foreground">
                  {flow.toEmails.join(', ')}
                </span>
              </div>
            )}
            {flow.ccEmails.length > 0 && (
              <div className="flex gap-2">
                <span className="w-12 shrink-0 font-medium text-muted-foreground">
                  <T context="email header CC field">CC:</T>
                </span>
                <span className="text-foreground">
                  {flow.ccEmails.join(', ')}
                </span>
              </div>
            )}
            {confirmBccEmails.length > 0 && (
              <div className="flex gap-2">
                <span className="w-12 shrink-0 font-medium text-muted-foreground">
                  <T context="email header BCC field">BCC:</T>
                </span>
                <span className="text-foreground">
                  {confirmBccEmails.join(', ')}
                </span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirm(false)}
              disabled={isSending}
            >
              <T context="button label, cancel send invoice confirmation">
                Cancel
              </T>
            </Button>
            <Button onClick={handleConfirmedSend} disabled={isSending}>
              {isSending ? (
                <>
                  <SpinnerIcon className="mr-1.5 h-4 w-4 animate-spin" />
                  <T context="button label, sending invoice in progress">
                    Sending...
                  </T>
                </>
              ) : (
                <T context="button label, confirm and send invoice">
                  Confirm & Send
                </T>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Send All confirm ─────────────────────────────────────────── */}
      <Dialog
        open={flow.showSendAllConfirm}
        onOpenChange={flow.setShowSendAllConfirm}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              <T context="bulk send confirm dialog title">Send all invoices?</T>
            </DialogTitle>
          </DialogHeader>
          {unvisitedInvoices.length > 0 ? (
            <p className="text-sm text-muted-foreground">
              <T context="bulk send confirm dialog body, warns about unreviewed invoices with no recipients set">
                <span className="font-medium text-foreground">
                  <Var>{unvisitedInvoices.length}</Var> of{' '}
                  <Var>{remainingCount}</Var>
                </span>{' '}
                remaining invoices haven&rsquo;t been reviewed and have no
                recipients set — they will be skipped. Invoices you reviewed
                will use the edits you made.
              </T>
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              <T context="bulk send confirm dialog body, all invoices reviewed">
                This will send{' '}
                <span className="font-medium text-foreground">
                  <Var>{remainingCount}</Var> invoices
                </span>{' '}
                using the edits you made. This action can&rsquo;t be undone.
              </T>
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => flow.setShowSendAllConfirm(false)}
              disabled={flow.isSendingAll}
            >
              <T context="button label, cancel bulk send confirm dialog">
                Cancel
              </T>
            </Button>
            <Button
              onClick={() => void handleSendAll()}
              disabled={flow.isSendingAll}
            >
              <T context="button label, confirm bulk send of remaining invoices">
                Send All
              </T>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
